/**
 * NeedsYouTab — the merged triage list (CRM UI streamline D-7).
 *
 * Classic split the human's work across three tabs: Review queue (eligibility
 * determinations + escalated questions + screening flags), Drafts (copilot
 * replies awaiting approval), and Escalations (everything else the agent
 * raised). Nothing told a recruiter which of the three had the oldest thing in
 * it, so the answer to "what do I do next" was to visit all three and compare.
 * This is one list, in one order, with the right pane the classic surfaces
 * already used for each kind of item.
 *
 * NOTHING IS RE-IMPLEMENTED. A queue item opens `DeterminationWorkspace`, a
 * draft opens `DraftCard`, an agent issue opens `EscalationCard` — the same
 * components, the same handler props, so approve/edit/reject and claim/resolve
 * behave identically to the surfaces they came from. The list's contents and
 * the tab badge come from ONE selector (needsYou.ts); this file never counts
 * anything itself.
 *
 * The [Needs action | Resolved] chips flip the page's escalations fetch. Reading
 * Resolved history must not move the badge or the KPI tile, and it cannot: both
 * read the page's LATCHED active summary, never the rows on screen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Bot, ChevronDown, ChevronLeft, ChevronRight, ClipboardCheck, HelpCircle, Loader2,
  MessageSquareText, ShieldAlert, XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { FilterBar, FilterChip, FilterSelect } from '../../../components/filters';
import { Alert } from '../../../components/ui';
import { RowLink } from '../../../components/shared';
import RecruiterBadge from '../../../components/RecruiterBadge';
import { touchTarget } from '../../../../ui/styles';
import { getPref, setPref, usePersistedState, useTeam } from '../../../lib';
import { ESCALATION_KIND_LABEL as KIND_LABEL } from '../../../components/escalationDisplay';
import { ATTENTION_SEVERE_KINDS } from '../../../components/lead/attentionSummary';
import DraftCard from '../../../components/agent/DraftCard';
import { OUTCOME_LABEL } from '../../../components/agent/DeterminationComposer';
import { ago, type DraftRow } from '../../../components/agent/shared';
import type { StudyOption } from '../../../types';
import { EscalationCard, type EscalationRow } from '../AgentConsole';
import {
  DET_STATUS_PILL,
  DeterminationWorkspace,
  type QueueDetermination,
  type QueueItem,
} from '../AgentReviewQueue';
import {
  needsYouRows, opsEscalations, shouldAutoSelect, shouldClearStaleSelection,
  type NeedsYou, type NeedsYouRow,
} from './needsYou';
import { useLgUp } from './useLgUp';

/** Re-exported so ConsoleV2 (and the page) name the queue module's own type. */
export type { QueueDetermination };

/** Shown until dismissed, once per device — the pref precedent (sidebar, view). */
const CLAIM_HINT_PREF = 'agentClaimHint';

export interface NeedsYouTabProps {
  needs: NeedsYou;
  /** The CURRENT escalations view, raw — the ops filter is applied here. */
  escalations: EscalationRow[];
  determinations: QueueDetermination[];
  escStatus: 'active' | 'resolved';
  onEscStatusChange: (s: 'active' | 'resolved') => void;
  studies: StudyOption[];
  loading: boolean;
  nowTs: number;
  busyId: string | null;
  /** `?review=<conversationId>` — the Today / lead-page deep link. */
  reviewParam: string | null;
  /** Open a conversation in the Conversations tab (the console owns the URL). */
  onOpenConversation: (conversationId: string) => void;
  onApprove: (t: DraftRow, text?: string) => Promise<void>;
  onReject: (t: DraftRow, reason: string) => Promise<void>;
  onEscalationAction: (id: string, action: 'claim' | 'resolve') => Promise<void>;
  onError: (msg: string) => void;
  onRefresh: () => void;
}

export default function NeedsYouTab({
  needs, escalations, determinations, escStatus, onEscStatusChange, studies, loading,
  nowTs, busyId, reviewParam, onOpenConversation, onApprove, onReject,
  onEscalationAction, onError, onRefresh,
}: NeedsYouTabProps) {
  // The Agent-issues group is LATCHED to the last "Needs action" fetch, for the
  // same reason the badge is: `escalations` refills with RESOLVED rows the moment
  // somebody looks at history, and a triage list that quietly swapped its
  // contents (and moved the selection) while hidden behind the other chip would
  // be lying about what still needs doing.
  const [activeEscalations, setActiveEscalations] = useState<EscalationRow[]>([]);
  useEffect(() => {
    // The open/claimed filter matches the server's status=active semantics; it
    // also guards the one-fetch window where escStatus has flipped back to
    // 'active' while `escalations` still holds the RESOLVED fetch (seam-4 F4) —
    // resolved rows can never enter the latch.
    if (escStatus === 'active') {
      setActiveEscalations(escalations.filter((e) => e.status === 'open' || e.status === 'claimed'));
    }
  }, [escStatus, escalations]);

  const ops = useMemo(() => opsEscalations(activeEscalations), [activeEscalations]);

  // Recruiter filter — narrows the three queue-item groups only (eligibility,
  // screening, questions). Drafts and agent issues are untouched: they were
  // never part of the "which recruiter owns this lead" ask, and the Needs-you
  // COUNT/badge upstream (ConsoleV2) reads the unfiltered `needs` prop, so a
  // local filter here can never move the tab badge or KPI tile.
  const { team } = useTeam();
  const [ownerFilter, setOwnerFilter] = useState('');
  const filteredNeeds = useMemo<NeedsYou>(() => {
    if (!ownerFilter) return needs;
    const matches = (i: QueueItem) => (ownerFilter === 'unassigned' ? !i.owner_user_id : i.owner_user_id === ownerFilter);
    return {
      ...needs,
      eligibility: needs.eligibility.filter(matches),
      screening: needs.screening.filter(matches),
      questions: needs.questions.filter(matches),
    };
  }, [needs, ownerFilter]);

  const rows = useMemo<NeedsYouRow[]>(
    () => needsYouRows(filteredNeeds, activeEscalations),
    [filteredNeeds, activeEscalations],
  );

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Deep link from Today / the lead page (?review=<conversation_id>): preselect
  // that conversation's row ONCE, then let the recruiter drive. The param is
  // read-only here — the console owns the URL.
  const [deepLinked, setDeepLinked] = useState(false);
  useEffect(() => {
    if (deepLinked || !reviewParam || !rows.length) return;
    const match = rows.find((r) => r.conversationId === reviewParam);
    if (match) setSelectedKey(match.key);
    setDeepLinked(true);
  }, [deepLinked, reviewParam, rows]);

  const selected = rows.find((r) => r.key === selectedKey) || null;
  // Which tier is on screen. A BEHAVIOR question, so it cannot be a class: the
  // panes below hide with `lg:`, but "do not pick a row for me" has no CSS.
  const lgUp = useLgUp();
  // Auto-select the oldest item so the LAPTOP's right pane never opens empty —
  // below `lg` the list is the landing view and picking a row would strand the
  // recruiter in a detail they never asked for. Every rule (tier, deep link, an
  // existing selection) lives in shouldAutoSelect, where it is tested. `lgUp`
  // is a dependency on purpose: crossing phone→laptop with nothing selected
  // fills the pane, and laptop→phone re-runs into the early return, so an open
  // item stays open (with the Back affordance below).
  useEffect(() => {
    // A dead key on a phone is dropped, never remembered — the keys are
    // conversation-scoped, so a remembered one would re-drill into that
    // conversation's NEXT item without a tap (see shouldClearStaleSelection).
    if (shouldClearStaleSelection(lgUp, selectedKey, !!selected)) {
      setSelectedKey(null);
      return;
    }
    const auto = shouldAutoSelect({
      lgUp,
      hasSelection: !!selected,
      rowCount: rows.length,
      deepLinkSettled: deepLinked,
      reviewParam,
    });
    if (auto) setSelectedKey(rows[0].key);
  }, [lgUp, selected, selectedKey, rows, deepLinked, reviewParam]);

  /** Drop the selection — the phone's Back, and the only caller. Selection is
   *  local state on this surface (`?review=` is read-only here: it SEEDS the
   *  first selection through the effect above and is never written), so this is
   *  the same state path a row click takes, with null instead of a key. No
   *  second clearing mechanism exists to keep in step. */
  const clearSelection = useCallback(() => setSelectedKey(null), []);

  // Keep the selected row visible — a deep link can land on a row well down a
  // list of five groups. `nearest` means an already-visible row does not move.
  const listRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedKey) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-item-key="${selectedKey}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedKey]);

  const [hintDone, setHintDone] = useState(() => getPref(CLAIM_HINT_PREF, '') === 'done');
  const dismissHint = () => { setPref(CLAIM_HINT_PREF, 'done'); setHintDone(true); };

  // The delivery strip is a log of what already happened, not something
  // owed — collapsible, and remembered per device like the sidebar/view prefs.
  const [detOpen, setDetOpen] = usePersistedState('agentDeterminationsOpen', true);

  const inFlight = determinations.filter((d) => d.status === 'submitted' || d.status === 'delivering');
  const recent = determinations.filter((d) => d.status !== 'submitted' && d.status !== 'delivering').slice(0, 12);

  // -- rows ----------------------------------------------------------------
  const rowCls = (key: string) =>
    `flex w-full min-h-[44px] items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:min-h-0 ${
      selectedKey === key
        ? 'border-primary-300 bg-primary-50 dark:border-primary-700 dark:bg-primary-900/20'
        : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60'
    }`;

  /** Groups 1–3: the review queue's own row, unchanged. */
  const queueRow = (i: QueueItem) => (
    <button
      key={i.key}
      type="button"
      data-item-key={i.key}
      aria-current={selectedKey === i.key}
      onClick={() => setSelectedKey(i.key)}
      className={rowCls(i.key)}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{i.contact_name || 'Unknown lead'}</span>
          {i.claimed && (
            <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">claimed</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
          <span className="truncate">
            {i.group === 'screening'
              ? `${i.flags?.length || 1} flagged answer${(i.flags?.length || 1) === 1 ? '' : 's'} · screening ongoing`
              : i.escalation ? (KIND_LABEL[i.escalation.kind] || i.escalation.kind) : 'Eligibility review'}
            {i.study_title ? ` · ${i.study_title}` : ''}
          </span>
        </div>
      </div>
      <RecruiterBadge ownerId={i.owner_user_id} />
      <span className="flex-shrink-0 text-[10px] text-gray-400">{ago(i.created_at)}</span>
      <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
    </button>
  );

  /** Group 4: a copilot reply waiting on a person. */
  const draftRow = (d: DraftRow) => {
    const key = `draft-${d.id}`;
    const suppressed = d.status === 'suppressed';
    return (
      <button
        key={key}
        type="button"
        data-item-key={key}
        aria-current={selectedKey === key}
        onClick={() => setSelectedKey(key)}
        className={rowCls(key)}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{d.contact_name || 'Lead'}</span>
            <span
              className={`inline-flex flex-shrink-0 items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium ${suppressed
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'}`}
            >
              {suppressed && <ShieldAlert className="h-3 w-3" />}
              {suppressed ? 'held by guardrails' : 'draft'}
            </span>
          </div>
          <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">{d.draft_body}</div>
        </div>
        <span className="flex-shrink-0 text-[10px] text-gray-400">{ago(d.created_at)}</span>
        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
      </button>
    );
  };

  /** Group 5: an agent issue — nothing to determine, but somebody must look. */
  const opsRow = (e: EscalationRow) => {
    const key = `ops-${e.id}`;
    // Red is reserved for the kinds the lead page also calls severe (ONE set,
    // imported); anything still open is amber; a resolved row is quiet.
    const tint = ATTENTION_SEVERE_KINDS.has(e.kind)
      ? 'text-red-500'
      : e.status === 'resolved' ? 'text-gray-400' : 'text-amber-500';
    return (
      <button
        key={key}
        type="button"
        data-item-key={key}
        aria-current={selectedKey === key}
        onClick={() => setSelectedKey(key)}
        className={rowCls(key)}
      >
        <AlertTriangle className={`h-3.5 w-3.5 flex-shrink-0 ${tint}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{e.contact_name || 'Lead'}</span>
            {e.status === 'claimed' && (
              <span className="rounded bg-blue-100 px-1 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">claimed</span>
            )}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
            {KIND_LABEL[e.kind] || e.kind}
            {e.payload?.reason ? ` · ${String(e.payload.reason)}` : ''}
          </div>
        </div>
        <span className="flex-shrink-0 text-[10px] text-gray-400">{ago(e.created_at)}</span>
        <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-600" />
      </button>
    );
  };

  const section = (
    Icon: LucideIcon,
    title: string,
    count: number,
    empty: string,
    body: ReactNode,
    hint?: string,
  ) => (
    <section>
      <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        <Icon className="h-3.5 w-3.5" /> {title}
        <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">{count}</span>
      </h3>
      {hint && count > 0 && (
        <p className="mb-1.5 text-[11px] leading-relaxed text-gray-400 dark:text-gray-500">{hint}</p>
      )}
      {count === 0 ? (
        <p className="rounded-lg border border-dashed border-gray-300 px-3 py-3 text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
          {empty}
        </p>
      ) : <div className="space-y-1.5">{body}</div>}
    </section>
  );

  return (
    <div className="mt-3">
      {/* 'Needs action' = open + claimed (M-12): a claimed row stays findable so
          its claimer can Resolve it. Resolved is history, and reading it must
          not move the badge — the console latches that number elsewhere. */}
      <FilterBar>
        <FilterChip
          active={escStatus === 'active'}
          onClick={() => onEscStatusChange('active')}
          title="Everything still waiting on a person — determinations, drafts, and agent issues"
        >
          Needs action
        </FilterChip>
        <FilterChip
          active={escStatus === 'resolved'}
          onClick={() => onEscStatusChange('resolved')}
          title="History: escalations that have already been handled. Reading this does not change the count."
        >
          Resolved
        </FilterChip>
        {escStatus === 'active' && (
          <FilterSelect
            value={ownerFilter}
            onChange={setOwnerFilter}
            label="Filter by assigned recruiter"
            title="Narrow eligibility reviews, screening flags, and escalated questions to one recruiter"
            auto
            className="max-w-[200px]"
          >
            <option value="">All recruiters</option>
            <option value="unassigned">Unassigned</option>
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </FilterSelect>
        )}
      </FilterBar>

      {!hintDone && (
        <Alert tone="info" size="sm" className="mt-3" onDismiss={dismissHint}>
          <span className="font-semibold">Claim</span> = &ldquo;I&rsquo;m on it&rdquo; — assigns the item to you, tells
          teammates it&rsquo;s taken, and stops any countdown (the agent will NOT answer for you once you claim).{' '}
          <span className="font-semibold">Resolve</span> = &ldquo;handled&rdquo; — clears it from the queue. Neither
          button texts the lead; you reply from the thread.
        </Alert>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" />
        </div>
      ) : (
        <>
          {/* Needs action — the merged list. Hidden rather than unmounted so a
              peek at Resolved never throws away a half-written determination. */}
          <div className={escStatus === 'active' ? 'mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[340px_minmax(0,1fr)]' : 'hidden'}>
            {/* Below `lg` the two panes share one column, so exactly one of
                them is on screen: the list until something is selected, the
                detail after (the board's drill-in, D-8's own shape). Both stay
                MOUNTED — coming back must land on the same scroll position and
                must never throw away a half-written determination. */}
            <div ref={listRef} className={`space-y-3 ${selected ? 'hidden lg:block' : ''}`}>
              {section(ClipboardCheck, 'Eligibility reviews', filteredNeeds.eligibility.length,
                ownerFilter ? 'No matches for this recruiter.' : 'No leads waiting on a determination.',
                filteredNeeds.eligibility.map(queueRow))}

              {section(XCircle, 'Screening flags', filteredNeeds.screening.length,
                ownerFilter ? 'No matches for this recruiter.' : 'No mid-screen answers flagged.',
                filteredNeeds.screening.map(queueRow),
                'An answer looks disqualifying mid-screen. The agent keeps the conversation going — you can end it early with a determination, or leave it to finish.')}

              {section(HelpCircle, 'Escalated questions', filteredNeeds.questions.length,
                ownerFilter ? 'No matches for this recruiter.' : 'Nothing escalated right now.',
                filteredNeeds.questions.map(queueRow))}

              {section(MessageSquareText, 'Drafts waiting', needs.draftItems.length,
                'No drafts waiting — in copilot mode every reply the agent wants to send waits here.',
                needs.draftItems.map(draftRow))}

              {section(AlertTriangle, 'Agent issues', ops.length,
                'The agent is running clean.', ops.map(opsRow),
                'Blocked drafts, stalls and errors — nothing to decide, but somebody should look.')}

              {/* The determination delivery strip — what the agent has sent (or
                  failed to send) on your behalf. Collapsible (state persists
                  per device) since it is a log of what already happened, not
                  something owed; the name is a RowLink so a recruiter can jump
                  straight to the lead's page without hunting for an action. */}
              {(inFlight.length > 0 || recent.length > 0) && (
                <section>
                  <button
                    type="button"
                    onClick={() => setDetOpen((v) => !v)}
                    aria-expanded={detOpen}
                    aria-controls="agent-determinations-body"
                    className="mb-1.5 flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <Bot className="h-3.5 w-3.5" /> Determinations
                    <span className="rounded-full bg-gray-100 px-1.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {inFlight.length + recent.length}
                    </span>
                    <ChevronDown
                      aria-hidden="true"
                      className={`ml-auto h-3.5 w-3.5 flex-shrink-0 transition-transform duration-150 ${detOpen ? '' : '-rotate-90'}`}
                    />
                  </button>
                  <div id="agent-determinations-body" className={detOpen ? 'space-y-1.5' : 'hidden'}>
                    {[...inFlight, ...recent].map((d) => (
                      <div key={d.id} className="rounded-lg border border-gray-200 bg-white px-2.5 py-2 dark:border-gray-700 dark:bg-gray-800">
                        <div className="flex items-center gap-2">
                          {d.contact_id ? (
                            <RowLink
                              to={`/crm/contacts/${d.contact_id}`}
                              title={`${d.contact_name || 'Lead'} — open the full lead page`}
                              className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900 hover:text-primary-700 hover:underline dark:text-gray-100 dark:hover:text-primary-400"
                            >
                              {d.contact_name || 'Lead'}
                            </RowLink>
                          ) : (
                            <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-900 dark:text-gray-100">{d.contact_name || 'Lead'}</span>
                          )}
                          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${DET_STATUS_PILL[d.status] || DET_STATUS_PILL.canceled}`}>
                            {d.status === 'delivering' ? 'sending…' : d.status}
                          </span>
                          <span className="text-[10px] text-gray-400">{ago(d.created_at)}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                          {OUTCOME_LABEL[d.outcome] || d.outcome}
                          {d.status === 'failed' && d.error ? <span className="text-red-600 dark:text-red-400"> — {d.error}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Right: the surface each kind of item already had. Nothing
                selected below `lg` means this pane has nothing to say and the
                list IS the view, so it steps out of the column entirely; from
                `lg` up it keeps the summary state it has always had. */}
            <div className={selected ? '' : 'hidden lg:block'}>
              {/* The way back to the list, phone/tablet only — from `lg` up
                  both panes are on screen and there is nowhere to go back to. */}
              <button
                type="button"
                onClick={clearSelection}
                className={`mb-2 inline-flex ${touchTarget} items-center gap-1.5 rounded text-sm font-medium text-gray-600 transition-colors duration-150 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-300 dark:hover:text-gray-100 lg:hidden`}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" /> All items
              </button>

              {!selected ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
                  <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">All caught up</p>
                  <p className="mx-auto mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">
                    Escalated questions, leads needing an eligibility call, drafts awaiting approval and agent issues all land here.
                  </p>
                </div>
              ) : selected.kind === 'queue' ? (
                <DeterminationWorkspace
                  key={selected.key}
                  item={selected.item}
                  studies={studies}
                  onOpenConversation={onOpenConversation}
                  onError={onError}
                  onSubmitted={onRefresh}
                />
              ) : selected.kind === 'draft' ? (
                <DraftCard
                  key={selected.key}
                  t={selected.draft}
                  busy={busyId === selected.draft.id}
                  nowTs={nowTs}
                  showContact
                  onApprove={onApprove}
                  onReject={onReject}
                  onViewThread={() => onOpenConversation(selected.draft.conversation_id)}
                />
              ) : (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                  <ul>
                    <EscalationCard
                      key={selected.key}
                      e={selected.escalation}
                      busy={busyId === selected.escalation.id}
                      nowTs={nowTs}
                      showContact
                      showBreakdown
                      onAction={onEscalationAction}
                      onViewThread={() => onOpenConversation(selected.escalation.conversation_id)}
                    />
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Resolved — read-only history, exactly what the old tab's Resolved
              chip showed (EscalationCard hides its verbs on a resolved row). */}
          <div className={escStatus === 'resolved' ? 'mt-3' : 'hidden'}>
            {escalations.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
                <ClipboardCheck className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Nothing resolved yet</p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                  Escalations you have handled show up here, with the full breakdown of what caused them.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {escalations.map((e) => (
                    <EscalationCard
                      key={e.id}
                      e={e}
                      busy={busyId === e.id}
                      nowTs={nowTs}
                      showContact
                      showBreakdown
                      onAction={onEscalationAction}
                      onViewThread={() => onOpenConversation(e.conversation_id)}
                    />
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
